import { useContext, useState } from "react"
import { useMutation, useLazyQuery, gql } from "@apollo/client"
import { useNavigate, Link } from "react-router-dom"
import { Formik, Form } from "formik"
import _ from "lodash"

// Helpers
import handleInitialValues from "./helpers/handleInitialValues"
import fieldValidationSchema from "./helpers/fieldValidationSchema"
import handleFieldConditional from "./helpers/handleFieldConditional"
import handleSubmitValues from "./helpers/handleSubmitValues"
import { trimTextValue } from "../../components/FieldTypes/helpers/trimTextValue"
import getFieldSubtypeOptions from "./helpers/getFieldSubtypeOptions"
import { ADD_FIELD, GET_FIELDS_LIBRARY } from "./queries"

// Comps
import TypeSelectOptions from "./components/TypeSelectOptions"

// Context
import { UserContext } from "../../context/userContext"

// Prime
import { InputText as Input } from "primereact/inputtext"
import { InputTextarea } from "primereact/inputtextarea"
import { Dropdown } from "primereact/dropdown"
import { Button } from "primereact/button"
import { SelectButton } from "primereact/selectbutton"

// Styles
import styles from "../../styles/Form.module.css"

export default function LibraryAddCustomField() {
  const userCtx = useContext(UserContext)
  const [isUnique, setIsUnique] = useState(true)

  const navigate = useNavigate()

  const [addField] = useMutation(ADD_FIELD)

  const CHECK_NAME_REPORT = gql`
    query CheckNameReport($enterprise_id: ID!, $field_name_report: String!) {
      getNameReport: checkNameReport(
        enterprise_id: $enterprise_id
        field_name_report: $field_name_report
      ) {
        data
      }
    }
  `

  const [checkNameReport, { loading }] = useLazyQuery(CHECK_NAME_REPORT, {
    fetchPolicy: "no-cache",
  })

  const fieldTypes = [
    { label: "Text", value: "text" },
    { label: "Textarea", value: "textarea" },
    { label: "Textarea Rich", value: "textarea_rich" },
    { label: "Number", value: "number" },
    { label: "Checkbox", value: "checkbox" },
    { label: "Dropdown", value: "dropdown" },
    { label: "Radio", value: "radio" },
    { label: "Date", value: "date" },
  ]

  const dateFieldSubTypes = getFieldSubtypeOptions("date")

  const handleOnChange = (e, formik) => {
    const { name, value } = e.target

    // Reset field options value when field type is changed from a select type
    const selectTypes = ["dropdown", "checkbox", "radio"]
    const isTypeSelect = selectTypes.includes(value)
    if (!isTypeSelect) {
      formik.setFieldValue("field_options", [])
    }

    // Reset field sub-type value when field type is changed
    if (value !== "date") {
      formik.setFieldValue("field_type_subtype", "")
    }

    // Set field type value
    formik.setFieldValue(name, value)
  }

  return (
    <div className="layout-full">
      <h1>Customize Field</h1>
      <Link
        to="/enterprise/library"
        style={{ marginBottom: 30, display: "inline-block" }}
        className="btn-sm"
      >
        Back to Library
      </Link>
      <Formik
        initialValues={handleInitialValues()}
        validationSchema={fieldValidationSchema}
        onSubmit={async (values) => {
          await addField({
            variables: {
              organization_id: userCtx.session.user.organization_id,
              enterprise_id: userCtx.session.user.enterprise_id,
              created_by: userCtx.session.user.staff_id,
              updated_by: userCtx.session.user.staff_id,
              ...handleSubmitValues(values),
              field_parent: "",
            },
            refetchQueries: [
              {
                query: GET_FIELDS_LIBRARY,
                variables: {
                  staff_id: userCtx.session.user.staff_id,
                  organization_id: userCtx.session.user.organization_id,
                  enterprise_id: userCtx.session.user.enterprise_id,
                },
              },
            ],
          })
          navigate("/enterprise/library")
        }}
      >
        {(formik) => {
          return (
            <Form className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label}>System field name:</label>
                <Input
                  name="field_name"
                  value={formik.values.field_name}
                  disabled
                  className={styles.value}
                />
              </div>

              <div className={styles.field}>
                {loading && <i className="pi pi-spin pi-spinner" />}
                <label className={styles.label}>Reporting field name:</label>
                <Input
                  name="field_name_report"
                  onChange={(e) => formik.handleChange(trimTextValue(e))}
                  onBlur={async (e) => {
                    checkNameReport({
                      variables: {
                        enterprise_id: userCtx.session.user.enterprise_id,
                        field_name_report: formik.values.field_name_report,
                      },
                      onCompleted: ({ getNameReport: { data } }) => {
                        if (_.isEmpty(data)) {
                          setIsUnique(true)
                        } else {
                          setIsUnique(false)
                          formik.setFieldValue("field_name_report", "")
                        }
                      },
                    })
                  }}
                  value={formik.values.field_name_report}
                  className={styles.value}
                />
              </div>
              {!isUnique && !loading && (
                <div className={styles.errFieldMsg}>
                  Field name already in use. Please choose another custom
                  reporting field name
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label}>Is required:</label>
                <SelectButton
                  name="field_required"
                  value={formik.values.field_required === true ? "Yes" : "No"}
                  onChange={(e) => {
                    const value = e.value === "Yes" ? true : false
                    formik.setFieldValue("field_required", value)
                  }}
                  onBlur={formik.handleBlur}
                  options={["No", "Yes"]}
                  aria-label="field_required"
                  className={styles.value}
                />
              </div>

              {formik.values.field_required && (
                <div className={styles.field}>
                  <label className={styles.label}>Message when required:</label>
                  <Input
                    name="field_required_msg"
                    onChange={(e) => formik.handleChange(trimTextValue(e))}
                    onBlur={formik.handleBlur}
                    value={formik.values.field_required_msg}
                    className={styles.value}
                  />
                  {formik.touched.field_required_msg &&
                    formik.errors.field_required_msg && (
                      <div className={styles.errFieldMsg}>
                        {formik.errors.field_required_msg}
                      </div>
                    )}
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label}>Data Dictionary:</label>
                <InputTextarea
                  type="textarea"
                  name="description"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.description}
                  className={styles.valueW75}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  Field label that displays on forms:
                </label>
                <Input
                  name="field_label"
                  onChange={(e) => formik.handleChange(trimTextValue(e))}
                  onBlur={formik.handleBlur}
                  value={formik.values.field_label}
                  className={styles.value}
                />
                {formik.touched.field_label && formik.errors.field_label && (
                  <div className={styles.errFieldMsg}>
                    {formik.errors.field_label}
                  </div>
                )}
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  Description for data entry on forms:
                </label>
                <InputTextarea
                  name="field_description"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.field_description}
                  className={styles.valueW75}
                />
                {formik.touched.field_description &&
                  formik.errors.field_description && (
                    <div className={styles.errFieldMsg}>
                      {formik.errors.field_description}
                    </div>
                  )}
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Field type:</label>
                <Dropdown
                  name="field_type"
                  onChange={(e) => handleOnChange(e, formik)}
                  onBlur={formik.handleBlur}
                  value={formik.values.field_type}
                  options={fieldTypes}
                  className={styles.value}
                />
                {formik.touched.field_type && formik.errors.field_type && (
                  <div className={styles.errFieldMsg}>
                    {formik.errors.field_type}
                  </div>
                )}
              </div>

              {formik.values.field_type === "date" && (
                <div className={styles.field}>
                  <label className={styles.label}>Field sub-type:</label>
                  <Dropdown
                    name="field_type_subtype"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.field_type_subtype}
                    options={dateFieldSubTypes}
                    className={styles.value}
                  />
                  {formik.touched.field_type_subtype &&
                    formik.errors.field_type_subtype && (
                      <div className={styles.errFieldMsg}>
                        {formik.errors.field_type_subtype}
                      </div>
                    )}
                </div>
              )}

              <TypeSelectOptions />

              <div className={styles.field}>
                <label className={styles.label}>Is conditional:</label>
                <SelectButton
                  name="field_conditional"
                  value={
                    formik.values.field_conditional === true ? "Yes" : "No"
                  }
                  onChange={(e) => {
                    const value = e.value === "Yes" ? true : false
                    formik.setFieldValue("field_conditional", value)
                  }}
                  onBlur={formik.handleBlur}
                  options={["No", "Yes"]}
                  aria-label="field_conditional"
                  className={styles.value}
                />
              </div>

              {handleFieldConditional(formik)}

              <Button
                className={styles.btnSubmit}
                label="Submit"
                icon="pi pi-check"
                loading={formik.isSubmitting}
                disabled={!formik.isValid}
                type="submit"
              />
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
